import React, { MouseEvent, ChangeEvent, useRef, useEffect, useState } from 'react';

import { injectIntl, IntlShape } from 'react-intl';

// axios
import axios from 'axios';
import { apiClient } from 'api-client';

// alerts
import { useSnackbar } from "notistack";

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setFinesForm } from 'redux/actions/finesActions';
import { setRefresh } from 'redux/actions/pageActions';
import { extractDataOrder } from 'common/extractData';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import FormField from "components/FormField";
import LoadingComponent from 'components/LoadingComponent';

// icons
import { orderIcon } from 'assets/icons';

import DOMPurify from "dompurify";

// types
import { OrderData, OrderDataForm, FinesDataForm } from 'types/typesRedux';

type Props = {
  intl: IntlShape,
  type?: 'closeOrder' | 'addFineOrder' | 'addFineReservation',
  orderId?: number,
  onAddFine?: (bool: boolean) => void,
  onCloseWindow?: (type: string, bool: boolean) => void,
}

const AddFine = ( {intl, type, orderId, onAddFine, onCloseWindow}: Props) => {
  
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const order = useSelector( (state: OrderData) => state.orderDetail.order );
  const finesForm = useSelector( (state: FinesDataForm) => state.allFines.finesForm );

  const convertHHMMtoISO = (time: string, date: Date = new Date()): string => {
    const [hours, minutes] = time.split(':').map(Number);
    date.setHours(hours, minutes, date.getSeconds(), date.getMilliseconds());
    return date.toISOString();
  };

 // set data from inputs to redux
  const handleInputDescription = ( e: ChangeEvent<HTMLInputElement>, newValue: string ) => {
    const fieldName = e.target.name;

    newValue = DOMPurify.sanitize( newValue );

    dispatch(setFinesForm({
      ...finesForm,
      [fieldName]: newValue,
    })
    )
  }; 

  const handleInputAmount = ( e: ChangeEvent<HTMLInputElement>, newValue: string ) => {
    const fieldName = e.target.name;

    const normalizedValue = newValue.replace(',', '.');
    const parsedValue = parseFloat(normalizedValue);

    dispatch(setFinesForm({
      ...finesForm,
      [fieldName]: parsedValue,
    })
    )
  }; 
 
  const handleInputTime = ( e: ChangeEvent<HTMLInputElement>, newValue: string ) => {
    const fieldName = e.target.name;

    dispatch(setFinesForm({
      ...finesForm,
      [fieldName]: newValue,
    })
    )
  };

  const closeWindow = () => {
    onCloseWindow( type, false );
    dispatch(setFinesForm({
      amount: null,
      description: '',
      time: '',
    }
    ));
  }

  // Handle fine /////////////////////////////
  const createFine = async ( id: number ) => {
    const cancelToken = axios.CancelToken.source()

    const url = type === 'addFineOrder' ? `/v1/orders/${id}/fines` : `/v1/reservations/${id}/fine`;

    try {
      const response = await apiClient.post(url ,{
        amount: finesForm.amount,
        description: finesForm.description,
        cancelToken:cancelToken.token
      });

      if (response) {
        setTimeout(() => {
          dispatch(setRefresh({ refreshFines: true }));
          if (type === 'addFineReservation') dispatch(setRefresh({ refresh: true }));
        }, 2000);        
        enqueueSnackbar(`${intl.formatMessage({id:'finesAlert.add.true'})}`, { variant: "success" });
      }
    } catch ( err ){
      enqueueSnackbar(`${intl.formatMessage({id:'finesAlert.add.false'})}`, { variant: "error" });
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    }
    return () => cancelToken.cancel()
  }

  // Handle close order /////////////////////////////
  const closeOrder = async ( id: number ) => {

    const isoString = convertHHMMtoISO(finesForm.time);
// console.log(isoString)

    const cancelToken = axios.CancelToken.source()

    try {
      await apiClient.post(`/v1/orders/${id}/close`,{
        amount: finesForm.amount,
        note: finesForm.description,
        finishedAt: isoString,
        cancelToken:cancelToken.token
      }
      ).then( ( response ) => {
        if ( response ) {
          enqueueSnackbar(`${intl.formatMessage({id:'orderAlert.closeOrder.true'})}`, { variant: 'success' });
          dispatch(setRefresh( { refreshOrder: true } ));
          if ( type === 'addFineReservation') dispatch(setRefresh( { refresh: true } ));
          // dispatch(setOrderForm( { status: '' } ));
        }
    });
    } catch ( err ){
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        enqueueSnackbar(`${intl.formatMessage({id:'orderAlert.closeOrder.false'})}`, { variant: 'error' });
      }
    };
    
    return () => cancelToken.cancel();
  }

  const handleSubmit = () => {
    if ( type === 'addFineOrder' ) {
      if ( finesForm.amount && finesForm.amount !== null && finesForm.description && finesForm.description !== '' ) {
        createFine( orderId );
        onCloseWindow( type, false )
      } else if ( !finesForm.description || finesForm.description === '' || !finesForm.amount || finesForm.amount === null ) {
        enqueueSnackbar(`${intl.formatMessage({id:'finesAlert.addDescriptionAmount.false'})}`, { variant: "error" });
      }
    } else if ( type === 'addFineReservation' ) {
      if ( finesForm.description && finesForm.description !== '' ) {
        createFine( orderId );
        onCloseWindow( type, false )
      } else if ( !finesForm.description || finesForm.description === '' || !finesForm.amount || finesForm.amount === null ) {
        enqueueSnackbar(`${intl.formatMessage({id:'finesAlert.addDescription.false'})}`, { variant: "error" });
      }
    } else if ( type === 'closeOrder' ) {
      if ( finesForm.amount && finesForm.amount !== null && finesForm.description && finesForm.description !== '' && finesForm.time && finesForm.time !== '' ) {
        closeOrder( orderId );
        onCloseWindow( type, false )
      } else if ( !finesForm.description || finesForm.description === '' || !finesForm.amount || finesForm.amount === null || !finesForm.time || finesForm.time === null ) {
        enqueueSnackbar(`${intl.formatMessage({id:'finesAlert.addDescriptionAmountTime.false'})}`, { variant: "error" });
      }
    }
  }

  const handleTitle = () => {
    if ( type === 'addFineOrder' || type === 'addFineReservation' ) return intl.formatMessage({id:'fines.addFine'})
    if ( type === 'closeOrder' ) return intl.formatMessage({id:'order.popup.title'})
  }


  // if ( localLoading === true ) {
  //   return <LoadingComponent />
  // }

  return (
    <Grid container sx={{ position: 'fixed', top: '0%', left: '0%',
      backgroundColor:"rgba( 0, 0, 0, 0.5 ) !important", 
      width:"100vw", height:"100vh", zIndex:"1300" }}
    >

      <Card
        sx={{ 
          minWidth: "24rem", width: "auto", maxWidth: "27rem",
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // backgroundColor: "#5ada55",
        }}
      >        

        <MDBox
          display="flex"
          flexDirection="column"
          p={2}
          m={0}
          sx={{ listStyle: "none" }}
        >    

          <MDBox sx= {{ display: "flex",
            flexDirection: "row", gap: "1rem" }} 
          >
            <MDBox className= "badge"
              sx={{ 
                // backgroundColor: "#17C063",
                p: 1, mt: -4, ml: 0,
                borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl, 
              }}
              display="grid"
              justifyContent="center"
              alignItems="center"
              bgColor="#17C063"
              color="white"
              width="4.5rem"
              height="4.5rem"
              shadow="md"
              borderRadius="lg"
              variant="gradient"  
              fontSize= "2.2rem"        
            >
              {orderIcon}
            </MDBox>

            <MDBox
              sx={{
                diplay: "flex", flexDirection: "column",
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "1.2rem", fontWeight: "700",
                }}
              >
                {handleTitle()}
              </MDTypography>
              <MDTypography
                sx={{
                  fontSize: "0.75rem", fontWeight: "400",
                  color: "#7B809A",
                }}
              >
                {intl.formatMessage({id:'fines.order'})}&nbsp;{order.id ?? ""}
              </MDTypography>
            </MDBox>
                            
          </MDBox>

          <MDBox
            sx={{
              diplay: "flex", flexDirection: "column",
              marginTop: "0.5rem",
            }}
          >
            <MDTypography
              sx={{
                fontSize: "0.875rem", fontWeight: "700",
              }}
            >
              {order.fullName ?? ""}
            </MDTypography>
            <MDTypography
              sx={{
                fontSize: "0.625rem", fontWeight: "400",
                color: "#7B809A",
              }}
            >
              {order.email ?? ""}
            </MDTypography>
          </MDBox>

          { (type === 'addFineOrder') ? (
            <MDBox
              sx={{
                display: "flex", flexDirection: "column", marginTop:"0.7rem",
                width: "60%",
              }}
            >          
              <MDInput 
                type="text"
                name='amount'
                multiline
                rows={1} 
                // value= { finesForm.amount ? finesForm.amount : '' }
                placeholder={intl.formatMessage({id:'fines.feeAmount'})}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputAmount(e, e.target.value)}
                InputProps={{
                  inputProps: {
                    onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      const allowedRegex = /^[0-9.,]$/;
                      if (!allowedRegex.test(e.key)) {
                        e.preventDefault();
                      }
                    },
                    inputMode: "decimal",
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D2D6DA',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#7B809A',
                    opacity: 1,
                  },
                }}
              />
            </MDBox>
          ) : type === 'closeOrder' ? (
            <MDBox
              sx={{
                display: "flex", flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column", marginTop:"0.7rem",
                  width: "47%",
                }}
              >          
                <MDInput 
                  type="time"
                  name='time'
                  multiline
                  // value= { finesForm.amount ? finesForm.amount : '' }
                  placeholder={intl.formatMessage({id:'order.popup.endedAt'})}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputTime(e, e.target.value)}
                  InputProps={{
                    inputProps: {
                      onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        const allowedCharsRegex = /^[0-9:]$/;
                        if (!allowedCharsRegex.test(e.key)) {
                          e.preventDefault();
                        }
                      },
                      pattern: "[0-9]{2}:[0-9]{2}",
                      inputMode: "numeric",
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D2D6DA',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#7B809A',
                      opacity: 1,
                    },
                  }}
                />
              </MDBox>
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column", marginTop:"0.7rem",
                  width: "47%",
                }}
              >
                <MDInput 
                  type="text"
                  name='amount'
                  multiline
                  rows={1} 
                  // value= { finesForm.amount ? finesForm.amount : '' }
                  placeholder={intl.formatMessage({id:'order.popup.price'})}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputAmount(e, e.target.value)}
                  InputProps={{
                    inputProps: {
                      onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        const allowedRegex = /^[0-9.,]$/;
                        if (!allowedRegex.test(e.key)) {
                          e.preventDefault();
                        }
                      },
                      inputMode: "decimal",
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D2D6DA',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#7B809A',
                      opacity: 1,
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          ) : null }

          <MDBox
            sx={{
              display: "flex", flexDirection: "column",
              marginTop:"1rem",
            }}
          >
            <MDInput
              type="text"
              name='description'
              multiline
              rows={4}
              // value= { finesForm.description ? finesForm.description : '' }
              placeholder={intl.formatMessage({id:'fines.detail.description'})}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputDescription(e, e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D2D6DA',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#7B809A',
                  opacity: 1,
                },
              }}
            />
          </MDBox>  

          <MDBox 
            sx={{
                display: "flex", flexDirection: "row", justifyContent: "flex-end", 
                gap: "0.5rem", maxWidth: "100%", marginBottom: "0.5rem", marginTop: "1rem",
            }}
          >
            <MDButton
              variant="outlined" 
              color="success" 
              // onClick={() => navigate(-1)}
              onClick={ closeWindow }
            >
              {intl.formatMessage({id:'button.cancel'})}
            </MDButton>
            <MDButton
              variant="contained" 
              color="warning" 
              // sx={{
              //   visibility: isVisible ? "visible" : "hidden", 
              // }}
              onClick={ handleSubmit }         
            >
              {(type === 'addFineOrder' || type === 'addFineReservation') 
                ? intl.formatMessage({id:'button.addFines'}) 
                : intl.formatMessage({id:'button.submitCloseOrder'})}
            </MDButton>
          </MDBox>
  
        </MDBox>
      </Card>
    </Grid>
  )
};

export default injectIntl(AddFine);
